// Resolves a human region (US state / province) for each tournament from its
// geofence geometry, so Spectators and Sponsors can search tournaments by
// region. Tournaments are authored in the dashboard app and carry no region
// text field — only boundary coordinates — so we reverse-geocode the boundary
// center via OpenStreetMap's Nominatim and cache the result in localStorage.
//
// Nominatim's usage policy caps callers at ~1 request/second; lookups are run
// sequentially with a small delay and every distinct location is fetched only
// once (keyed by rounded lat/lng and persisted across sessions).
import React from "react";
import { tournamentGeoCenter } from "../utils/geofence";

const CACHE_KEY = "the99.tournamentRegionCache.v1";

function readCache() {
  try {
    return JSON.parse(localStorage.getItem(CACHE_KEY) || "{}");
  } catch {
    return {};
  }
}

function writeCache(obj) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(obj));
  } catch {
    /* storage full / unavailable — non-fatal, we just re-fetch next time */
  }
}

// Round to ~1km so nearby tournaments share a cache entry (and one lookup).
function centerKey(center) {
  if (!center || !Number.isFinite(center.lat) || !Number.isFinite(center.lng)) return "";
  return `${center.lat.toFixed(2)}|${center.lng.toFixed(2)}`;
}

async function reverseGeocodeState(lat, lng) {
  const url =
    `https://nominatim.openstreetmap.org/reverse` +
    `?format=jsonv2&lat=${encodeURIComponent(lat)}` +
    `&lon=${encodeURIComponent(lng)}` +
    `&zoom=8&addressdetails=1`;
  const res = await fetch(url, {
    headers: { Accept: "application/json", "Accept-Language": "en-US,en" },
  });
  if (!res.ok) return null;
  const payload = await res.json();
  const address = payload?.address || {};
  return address.state || address.region || address.province || null;
}

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * @param {Array<{id: string}>} tournaments Tournament docs (with geofence).
 * @returns {{ regionByTournamentId: Record<string,string>, regions: string[] }}
 */
export default function useTournamentRegions(tournaments) {
  const [cache, setCache] = React.useState(() => readCache());

  const { keyByTournament, coordsByKey } = React.useMemo(() => {
    const keyMap = {};
    const coordsMap = {};
    (tournaments || []).forEach((t) => {
      if (!t?.id) return;
      const center = tournamentGeoCenter(t);
      const key = centerKey(center);
      if (!key) return;
      keyMap[t.id] = key;
      coordsMap[key] = center;
    });
    return { keyByTournament: keyMap, coordsByKey: coordsMap };
  }, [tournaments]);

  // Reverse-geocode any location we haven't resolved yet, one at a time.
  const pendingKeys = React.useMemo(
    () => Object.keys(coordsByKey).filter((k) => cache[k] === undefined),
    [coordsByKey, cache]
  );
  const pendingSignature = pendingKeys.join(",");

  React.useEffect(() => {
    if (!pendingSignature) return undefined;
    let cancelled = false;
    (async () => {
      const resolved = {};
      for (const key of pendingSignature.split(",")) {
        if (cancelled) return;
        const coords = coordsByKey[key];
        if (!coords) continue;
        let state = null;
        try {
          state = await reverseGeocodeState(coords.lat, coords.lng);
        } catch {
          /* network error — leave unresolved so we retry later */
        }
        resolved[key] = state || null;
        await delay(1100);
      }
      if (cancelled || Object.keys(resolved).length === 0) return;
      setCache((prev) => {
        const next = { ...prev, ...resolved };
        writeCache(next);
        return next;
      });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSignature]);

  const regionByTournamentId = React.useMemo(() => {
    const map = {};
    Object.entries(keyByTournament).forEach(([tid, key]) => {
      const region = cache[key];
      if (region) map[tid] = region;
    });
    return map;
  }, [keyByTournament, cache]);

  const regions = React.useMemo(
    () =>
      Array.from(new Set(Object.values(regionByTournamentId).filter(Boolean))).sort((a, b) =>
        a.localeCompare(b)
      ),
    [regionByTournamentId]
  );

  return { regionByTournamentId, regions };
}
